import { useEffect, useState } from 'react';
import { FiCheckCircle, FiXCircle, FiDollarSign, FiAlertCircle, FiInfo, FiX } from 'react-icons/fi';
import { apiRequest } from '../utils/auth';
import { useEscapeToClose } from '../utils/useEscapeToClose';
import Pagination from '../components/Pagination';

interface PayoutAttempt {
  attemptedAt: string;
  error: string;
  approvedBy?: string;
  source: 'admin_approve' | 'webhook';
}

interface PayoutStatusHistoryEntry {
  status: string;
  timestamp: string;
  actor?: string;
  source: 'user' | 'admin' | 'paypal_api' | 'paypal_webhook' | 'system';
  note?: string;
  paypalBatchId?: string;
  paypalItemId?: string;
}

interface Payout {
  payoutId: string;
  userId: string;
  userName?: string;
  diamondAmount: number;
  /**
   * Transaction fee withheld from the payout, in diamonds. The user's balance was
   * debited the full `diamondAmount`; `currencyAmount` is net of this. 0 on
   * fee-waived payouts and on any created before the fee existed.
   */
  feeDiamonds?: number;
  currencyAmount: number;
  currency: string;
  paypalEmail: string;
  paypalBatchId?: string;
  paypalItemId?: string;
  status: string;
  failureReason?: string;
  rejectionReason?: string;
  attemptHistory?: PayoutAttempt[];
  statusHistory?: PayoutStatusHistoryEntry[];
  approvedBy?: string;
  approvedAt?: string;
  rejectedBy?: string;
  rejectedAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface PaginationState {
  total: number;
  limit: number;
  skip: number;
}

export default function PayoutRequestsPage() {
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({ total: 0, limit: 20, skip: 0 });
  const [statusFilter, setStatusFilter] = useState('pending_approval');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [rejectPayoutId, setRejectPayoutId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [detailPayout, setDetailPayout] = useState<Payout | null>(null);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});

  // Escape backs out of either dialog — the reject prompt drops its draft reason
  // exactly as the Cancel button does.
  useEscapeToClose(!!rejectPayoutId, () => { setRejectPayoutId(null); setRejectReason(''); });
  useEscapeToClose(!!detailPayout, () => setDetailPayout(null));

  const STATUS_TABS = [
    { key: '', label: 'All' },
    { key: 'pending_approval', label: 'Pending Approval' },
    { key: 'pending', label: 'Pending' },
    { key: 'processing', label: 'Processing' },
    { key: 'completed', label: 'Completed' },
    { key: 'failed', label: 'Failed' },
    { key: 'rejected', label: 'Rejected' },
  ];

  const fetchStatusCounts = async () => {
    try {
      const response = await apiRequest<{ success: boolean; counts: Record<string, number> }>('/api/admin/payouts/status-counts');
      setStatusCounts(response.counts);
    } catch {
      // non-critical, tabs just won't show counts
    }
  };

  const fetchPayouts = async (skip = 0) => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        limit: '20',
        skip: skip.toString(),
        ...(statusFilter && { status: statusFilter }),
      });
      const response = await apiRequest<{
        success: boolean;
        payouts: Payout[];
        total: number;
        limit: number;
        skip: number;
      }>(`/api/admin/payouts?${params}`);
      setPayouts(response.payouts);
      setPagination({ total: response.total, limit: response.limit, skip: response.skip });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch payouts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayouts(0);
    fetchStatusCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleApprove = async (payoutId: string) => {
    if (!window.confirm('Approve this payout? This will send money via PayPal.')) return;
    setActionLoading(payoutId);
    try {
      await apiRequest(`/api/admin/payouts/${payoutId}/approve`, { method: 'POST' });
      fetchPayouts(pagination.skip);
      fetchStatusCounts();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!rejectPayoutId) return;
    setActionLoading(rejectPayoutId);
    try {
      await apiRequest(`/api/admin/payouts/${rejectPayoutId}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason: rejectReason || undefined }),
      });
      setRejectPayoutId(null);
      setRejectReason('');
      fetchPayouts(pagination.skip);
      fetchStatusCounts();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to reject');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, { bg: string; color: string }> = {
      pending_approval: { bg: 'var(--color-warning-strong)', color: '#fff' },
      pending: { bg: 'var(--color-accent-alt)', color: '#fff' },
      processing: { bg: '#2980b9', color: '#fff' },
      completed: { bg: 'var(--color-success-strong)', color: '#fff' },
      failed: { bg: 'var(--color-danger-strong)', color: '#fff' },
      rejected: { bg: 'var(--color-subtle)', color: '#fff' },
    };
    const c = colors[status] || { bg: 'var(--color-subtle)', color: '#fff' };
    return (
      <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: c.bg, color: c.color, fontSize: '12px', fontWeight: 500 }}>
        {status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
      </span>
    );
  };

  const formatCurrency = (amount: number, currency: string) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;

  return (
    <div className="page">
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>Payout Requests</h1>
        <button
          onClick={() => { fetchPayouts(0); fetchStatusCounts(); }}
          style={{ padding: '8px 16px', backgroundColor: 'var(--color-success-strong)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
        >
          Refresh
        </button>
      </div>

      {/* Status Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '1px solid var(--color-border)', overflowX: 'auto' }}>
        {STATUS_TABS.map((tab) => {
          const isActive = statusFilter === tab.key;
          const count = tab.key === '' ? (statusCounts.all ?? 0) : (statusCounts[tab.key] ?? 0);
          return (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              style={{
                padding: '10px 16px',
                border: 'none',
                borderBottom: isActive ? '2px solid var(--color-accent)' : '2px solid transparent',
                backgroundColor: 'transparent',
                color: isActive ? 'var(--color-text)' : 'var(--color-muted)',
                fontWeight: isActive ? 600 : 400,
                cursor: 'pointer',
                fontSize: '14px',
                whiteSpace: 'nowrap',
                transition: 'color 0.15s, border-color 0.15s',
              }}
            >
              {tab.label} ({count})
            </button>
          );
        })}
      </div>

      {error && (
        <div style={{ padding: '12px', backgroundColor: 'var(--color-danger-surface)', color: 'var(--color-danger)', borderRadius: '4px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-muted)' }}>Loading payouts...</div>
      ) : payouts.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-muted)' }}>
          <FiDollarSign size={48} style={{ marginBottom: '12px', opacity: 0.5 }} />
          <p>No payout requests found</p>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '12px', color: 'var(--color-muted)', fontSize: '14px' }}>
            Showing {payouts.length} of {pagination.total} payouts
          </div>
          <div style={{ backgroundColor: 'var(--color-surface)', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: 'var(--color-surface-hover)' }}>
                <tr>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>Date</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>User</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>Diamonds</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>Amount</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>PayPal</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {payouts.map((p) => (
                  <tr key={p.payoutId} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '12px', fontSize: '13px', color: 'var(--color-muted)' }}>{formatDate(p.createdAt)}</td>
                    <td style={{ padding: '12px', fontSize: '13px', color: 'var(--color-text)' }} title={p.userId}>{p.userName || p.userId.slice(0, 12) + '...'}</td>
                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: 'var(--color-text)' }}>{p.diamondAmount.toLocaleString()}</td>
                    <td style={{ padding: '12px', textAlign: 'right', color: 'var(--color-text)' }}>{formatCurrency(p.currencyAmount, p.currency)}</td>
                    <td style={{ padding: '12px', fontSize: '13px', color: 'var(--color-muted)' }}>{p.paypalEmail}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>{getStatusBadge(p.status)}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
                        <button
                          onClick={() => setDetailPayout(p)}
                          style={{ padding: '4px 8px', backgroundColor: 'transparent', color: 'var(--color-muted)', border: '1px solid var(--color-border)', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                          title="View details"
                        >
                          <FiInfo size={14} /> Details
                        </button>
                        {p.status === 'pending_approval' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
                          {p.failureReason && (
                            <span style={{ fontSize: '11px', color: 'var(--color-warning-strong)' }} title={p.failureReason}>
                              <FiAlertCircle size={12} /> Previous attempt failed
                            </span>
                          )}
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                            <button
                              onClick={() => handleApprove(p.payoutId)}
                              disabled={actionLoading === p.payoutId}
                              style={{ padding: '4px 10px', backgroundColor: 'var(--color-success-strong)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                            >
                              <FiCheckCircle size={14} /> Approve
                            </button>
                            <button
                              onClick={() => setRejectPayoutId(p.payoutId)}
                              disabled={actionLoading === p.payoutId}
                              style={{ padding: '4px 10px', backgroundColor: 'var(--color-danger-strong)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                            >
                              <FiXCircle size={14} /> Reject
                            </button>
                          </div>
                        </div>
                      )}
                      {p.status === 'failed' && p.failureReason && (
                        <span style={{ fontSize: '12px', color: 'var(--color-danger)' }} title={p.failureReason}>
                          <FiAlertCircle size={14} /> {p.failureReason.slice(0, 30)}
                        </span>
                      )}
                      {p.status === 'rejected' && p.rejectionReason && (
                        <span style={{ fontSize: '12px', color: 'var(--color-muted)' }} title={p.rejectionReason}>
                          {p.rejectionReason.slice(0, 30)}
                        </span>
                      )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => fetchPayouts((page - 1) * pagination.limit)}
          />
        </>
      )}

      {/* Reject Modal */}
      {rejectPayoutId && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'var(--color-surface)', borderRadius: '8px', padding: '24px', width: '400px', maxWidth: '90vw', border: '1px solid var(--color-border)' }}>
            <h3 style={{ margin: '0 0 16px', color: 'var(--color-text)' }}>Reject Payout</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection (optional)"
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--color-border)', backgroundColor: 'var(--color-input)', color: 'var(--color-text)', minHeight: '80px', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button
                onClick={() => { setRejectPayoutId(null); setRejectReason(''); }}
                style={{ padding: '8px 16px', border: '1px solid var(--color-border)', borderRadius: '4px', backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text)', cursor: 'pointer', fontSize: '14px' }}
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={actionLoading === rejectPayoutId}
                style={{ padding: '8px 16px', backgroundColor: 'var(--color-danger-strong)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detailPayout && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'var(--color-surface)', borderRadius: '8px', padding: '24px', width: '560px', maxWidth: '90vw', maxHeight: '80vh', overflow: 'auto', border: '1px solid var(--color-border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: 'var(--color-text)' }}>Payout Details</h3>
              <button onClick={() => setDetailPayout(null)} style={{ background: 'none', border: 'none', color: 'var(--color-muted)', cursor: 'pointer', padding: '4px' }}>
                <FiX size={20} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '8px 12px', fontSize: '13px', marginBottom: '20px' }}>
              <span style={{ color: 'var(--color-muted)' }}>Payout ID</span>
              <span style={{ color: 'var(--color-text)', fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-all' }}>{detailPayout.payoutId}</span>
              <span style={{ color: 'var(--color-muted)' }}>User</span>
              <span style={{ color: 'var(--color-text)' }}>{detailPayout.userName || detailPayout.userId}</span>
              <span style={{ color: 'var(--color-muted)' }}>User ID</span>
              <span style={{ color: 'var(--color-text)', fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-all' }}>{detailPayout.userId}</span>
              <span style={{ color: 'var(--color-muted)' }}>Status</span>
              <span>{getStatusBadge(detailPayout.status)}</span>
              <span style={{ color: 'var(--color-muted)' }}>Diamonds</span>
              <span style={{ color: 'var(--color-text)' }}>{detailPayout.diamondAmount.toLocaleString()}</span>
              {(detailPayout.feeDiamonds ?? 0) > 0 && <>
                <span style={{ color: 'var(--color-muted)' }}>Transaction fee</span>
                <span style={{ color: 'var(--color-warning-strong)' }}>
                  −{(detailPayout.feeDiamonds ?? 0).toLocaleString()} diamonds (paid on{' '}
                  {(detailPayout.diamondAmount - (detailPayout.feeDiamonds ?? 0)).toLocaleString()})
                </span>
              </>}
              <span style={{ color: 'var(--color-muted)' }}>Amount</span>
              <span style={{ color: 'var(--color-text)' }}>{formatCurrency(detailPayout.currencyAmount, detailPayout.currency)}</span>
              <span style={{ color: 'var(--color-muted)' }}>PayPal Email</span>
              <span style={{ color: 'var(--color-text)' }}>{detailPayout.paypalEmail}</span>
              {detailPayout.paypalBatchId && <>
                <span style={{ color: 'var(--color-muted)' }}>Batch ID</span>
                <span style={{ color: 'var(--color-text)', fontFamily: 'monospace', fontSize: '12px' }}>{detailPayout.paypalBatchId}</span>
              </>}
              <span style={{ color: 'var(--color-muted)' }}>Created</span>
              <span style={{ color: 'var(--color-text)' }}>{formatDate(detailPayout.createdAt)}</span>
              <span style={{ color: 'var(--color-muted)' }}>Updated</span>
              <span style={{ color: 'var(--color-text)' }}>{formatDate(detailPayout.updatedAt)}</span>
              {detailPayout.approvedBy && <>
                <span style={{ color: 'var(--color-muted)' }}>Approved By</span>
                <span style={{ color: 'var(--color-text)', fontFamily: 'monospace', fontSize: '12px' }}>{detailPayout.approvedBy}</span>
              </>}
              {detailPayout.approvedAt && <>
                <span style={{ color: 'var(--color-muted)' }}>Approved At</span>
                <span style={{ color: 'var(--color-text)' }}>{formatDate(detailPayout.approvedAt)}</span>
              </>}
              {detailPayout.failureReason && <>
                <span style={{ color: 'var(--color-muted)' }}>Last Error</span>
                <span style={{ color: 'var(--color-danger)' }}>{detailPayout.failureReason}</span>
              </>}
              {detailPayout.rejectionReason && <>
                <span style={{ color: 'var(--color-muted)' }}>Rejection Reason</span>
                <span style={{ color: 'var(--color-danger)' }}>{detailPayout.rejectionReason}</span>
              </>}
            </div>

            {/* Status History Timeline */}
            {detailPayout.statusHistory && detailPayout.statusHistory.length > 0 && (
              <div>
                <h4 style={{ margin: '0 0 12px', color: 'var(--color-text)', fontSize: '14px' }}>
                  <FiInfo size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  Status History ({detailPayout.statusHistory.length})
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {[...detailPayout.statusHistory].reverse().map((entry, i) => (
                    <div key={i} style={{ padding: '10px 12px', backgroundColor: 'var(--color-input)', borderRadius: '6px', border: '1px solid var(--color-border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--color-muted)' }}>
                          {formatDate(entry.timestamp)}
                        </span>
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                          <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '3px', backgroundColor: entry.status === 'completed' ? 'var(--color-success-surface)' : entry.status === 'rejected' || entry.status === 'failed' ? '#3d1a1a' : entry.status === 'processing' ? 'var(--color-info-surface)' : 'var(--color-warning-surface)', color: entry.status === 'completed' ? 'var(--color-success)' : entry.status === 'rejected' || entry.status === 'failed' ? 'var(--color-danger)' : entry.status === 'processing' ? 'var(--color-accent)' : 'var(--color-warning-strong)' }}>
                            {entry.status}
                          </span>
                          <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '3px', backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-muted)' }}>
                            {entry.source}
                          </span>
                        </div>
                      </div>
                      {entry.note && <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginTop: '4px' }}>{entry.note}</div>}
                      {entry.actor && (
                        <div style={{ fontSize: '11px', color: 'var(--color-muted)', marginTop: '4px' }}>
                          By: <span style={{ fontFamily: 'monospace' }}>{entry.actor}</span>
                        </div>
                      )}
                      {entry.paypalBatchId && (
                        <div style={{ fontSize: '11px', color: 'var(--color-muted)', marginTop: '2px' }}>
                          Batch: <span style={{ fontFamily: 'monospace' }}>{entry.paypalBatchId}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Legacy Attempt History (for old records without statusHistory) */}
            {(!detailPayout.statusHistory || detailPayout.statusHistory.length === 0) && detailPayout.attemptHistory && detailPayout.attemptHistory.length > 0 && (
              <div>
                <h4 style={{ margin: '0 0 12px', color: 'var(--color-text)', fontSize: '14px' }}>
                  <FiAlertCircle size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  Attempt History ({detailPayout.attemptHistory.length})
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {detailPayout.attemptHistory.map((attempt, i) => (
                    <div key={i} style={{ padding: '10px 12px', backgroundColor: 'var(--color-input)', borderRadius: '6px', border: '1px solid var(--color-border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--color-muted)' }}>
                          {formatDate(attempt.attemptedAt)}
                        </span>
                        <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '3px', backgroundColor: attempt.source === 'webhook' ? 'var(--color-info-surface)' : 'var(--color-warning-surface)', color: attempt.source === 'webhook' ? 'var(--color-accent)' : 'var(--color-warning-strong)' }}>
                          {attempt.source === 'webhook' ? 'Webhook' : 'Admin Approve'}
                        </span>
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--color-danger)' }}>{attempt.error}</div>
                      {attempt.approvedBy && (
                        <div style={{ fontSize: '11px', color: 'var(--color-muted)', marginTop: '4px' }}>
                          By: <span style={{ fontFamily: 'monospace' }}>{attempt.approvedBy}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(!detailPayout.statusHistory || detailPayout.statusHistory.length === 0) && (!detailPayout.attemptHistory || detailPayout.attemptHistory.length === 0) && (
              <div style={{ padding: '16px', textAlign: 'center', color: 'var(--color-muted)', fontSize: '13px' }}>
                No history recorded
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
